<script setup lang="ts">
    import { InputRow, Select, SectionHeader } from '@/components/ui/form';
    import { Input } from '@/components/ui/input';
    import { Label } from '@/components/ui/label';
    import { usePermissions } from '@/composables/usePermissions';
    import Permission from '@/enums/Permission';
    import FetchFromEconomicButton from '@/components/companies/FetchFromEconomicButton.vue';
    import { CheckboxGroupRoot, Separator, useFilter } from 'reka-ui';
    import { computed, ref } from 'vue';
    import { EconomicAccount, EconomicDimension } from '@/types';
    import SearchInput from '@/components/SearchInput.vue';
    import { Checkbox } from '@/components/ui/checkbox';

    const { hasPermission } = usePermissions();

    const props = withDefaults(
        defineProps<{
            form: any;
            voucherAccounts?: EconomicAccount[];
            isPendingVoucherAccounts?: boolean;
            dimensions?: EconomicDimension[];
            isPendingDimensions?: boolean;
        }>(),
        {
            voucherAccounts: () => [],
            dimensions: () => [],
        },
    );

    const emit = defineEmits<{
        (e: 'accounts-loaded', accounts: EconomicAccount[]): void;
    }>();

    const handleAccountsLoaded = (accounts: EconomicAccount[]) => {
        emit('accounts-loaded', accounts);
    };

    const searchTerm = ref('');
    const { contains } = useFilter({ sensitivity: 'base' });

    const filteredVoucherAccounts = computed(() =>
        props.voucherAccounts.filter(
            (account: EconomicAccount) =>
                contains(account.name, searchTerm.value) ||
                contains(account.accountNumber.toString(), searchTerm.value),
        ),
    );

    const allSelected = computed({
        get: () => props.form.economic_accounts.length === props.voucherAccounts.length,
        set: () => toggleAll(),
    });

    const toggleAll = () => {
        props.form.economic_accounts = allSelected.value
            ? []
            : props.voucherAccounts.map((account: EconomicAccount) => account.id);
    };

    const getDimensionEnabled = (dimension: any) => {
        return dimension.is_enabled ?? false;
    };

    const setDimensionEnabled = (dimension: any, enabled: boolean | string) => {
        const isEnabled = Boolean(enabled);
        dimension.is_enabled = isEnabled;
        
        // Toggle all dimension values when dimension is toggled
        if (dimension.values && Array.isArray(dimension.values)) {
            dimension.values.forEach((value: any) => {
                value.is_enabled = isEnabled;
            });
        }
    };

    const getDimensionValueEnabled = (value: any) => {
        return value.is_enabled ?? false;
    };

    const setDimensionValueEnabled = (value: any, enabled: boolean | string) => {
        value.is_enabled = Boolean(enabled);
    };

</script>

<template>
    <div class="space-y-5">
        <SectionHeader>{{ $t('companies.setup_tab_text') }}</SectionHeader>
        <div class="grid gap-3 sm:grid-cols-2">
            <InputRow
                :form="form"
                name="endpoint"
                label="companies.form.select"
            >
                <Select
                    v-model="form.endpoint"
                    :options="[
                        { value: 'plain', label: 'companies.plain_account' },
                        { value: 'economic', label: 'companies.economic_account' },
                    ]"
                    :translate="true"
                />
            </InputRow>
            <InputRow
                :form="form"
                name="api_token"
                label="companies.api_token"
            >
                <div class="flex items-center gap-1">
                    <Input
                        id="api_token"
                        v-model="form.api_token"
                        autocomplete="api_token"
                        :placeholder="$t('companies.api_token')"
                    />
                    <FetchFromEconomicButton
                        v-if="hasPermission(Permission.COMPANIES_UPDATE)"
                        :form="form"
                        @accounts-loaded="handleAccountsLoaded"
                    />
                </div>
            </InputRow>
            <InputRow
                :form="form"
                name="economic_number"
                label="companies.form.economic_number"
            >
                <Input
                    id="economic_number"
                    v-model="form.economic_number"
                    autocomplete="economic_number"
                    :placeholder="$t('companies.form.economic_number')"
                    readonly
                />
            </InputRow>
            <InputRow
                :form="form"
                name="website"
                label="companies.form.website"
            >
                <Input
                    id="website"
                    v-model="form.website"
                    autocomplete="website"
                    :placeholder="$t('companies.form.website')"
                />
            </InputRow>
        </div>

        <SectionHeader>{{ $t('companies.accounts') }}</SectionHeader>
        <div
            v-if="voucherAccounts.length > 0"
            class="flex flex-col gap-3"
        >
            <SearchInput
                :placeholder="$t('forms.search')"
                v-model="searchTerm"
            />
            <div class="flex flex-row items-center gap-2">
                <Label class="flex flex-row items-center gap-2 font-normal">
                    <Checkbox v-model="allSelected" />
                    <span>{{
                        allSelected ? $t('forms.unselect_all') : $t('forms.select_all')
                    }}</span>
                </Label>
                <Separator
                    orientation="vertical"
                    class="h-2.5!"
                />
                <span class="text-xs font-bold">
                    {{ form.economic_accounts.length }}
                    {{ $t('forms.selected_options') }}
                </span>
            </div>
            <CheckboxGroupRoot
                v-model="form.economic_accounts"
                class="grid max-h-[300px] grid-cols-2 gap-2 overflow-y-auto"
            >
                <Label
                    v-for="account in filteredVoucherAccounts"
                    :key="account.id"
                    class="flex flex-row items-center gap-2 font-normal"
                >
                    <Checkbox :value="account.id" />
                    <span>{{ account.accountNumber }} - {{ account.name }}</span>
                </Label>
            </CheckboxGroupRoot>
        </div>
        <div
            v-else
            class="text-muted-foreground text-xs"
        >
            {{ $t('companies.no_voucher_accounts') }}
        </div>

        <SectionHeader>{{ $t('companies.dimensions') }}</SectionHeader>
        <div
            v-if="form.economic_dimensions && form.economic_dimensions.length > 0"
            class="flex flex-col gap-3"
        >
            <div class="max-h-[300px] overflow-y-auto space-y-3">
                <div
                    v-for="(dimension, index) in form.economic_dimensions"
                    :key="dimension.id"
                    class="space-y-2"
                    :class="{ 'pb-3 border-b border-border/50': Number(index) < form.economic_dimensions.length - 1 }"
                >
                    <Label class="flex flex-row items-center gap-2 font-normal">
                        <Checkbox
                            :model-value="getDimensionEnabled(dimension)"
                            @update:model-value="setDimensionEnabled(dimension, $event)"
                        />
                        <span>{{ dimension.name }}</span>
                    </Label>
                    <div
                        v-if="dimension.values && dimension.values.length > 0"
                        class="pl-6 grid grid-cols-2 gap-2"
                    >
                        <Label
                            v-for="value in dimension.values"
                            :key="value.id"
                            class="flex flex-row items-center gap-2 font-normal"
                        >
                            <Checkbox
                                :model-value="getDimensionValueEnabled(value)"
                                @update:model-value="setDimensionValueEnabled(value, $event)"
                            />
                            <span>{{ value.key }} - {{ value.name }}</span>
                        </Label>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-else-if="isPendingDimensions"
            class="text-muted-foreground text-xs"
        >
            {{ $t('companies.loading_dimensions') }}
        </div>
        <div
            v-else
            class="text-muted-foreground text-xs"
        >
            {{ $t('companies.no_dimensions') }}
        </div>
    </div>
</template>
